'use client';

// ============================================================
// ScanFeeScreen : scanner générique (inbox back-office, duplicata de caisse)
// ============================================================
// Photographie d'un document, détection des bords (modèle ML U²-Net),
// réglage manuel des 4 coins, redressement (OpenCV), empilement des pages,
// puis envoi (POST /hr/portal/documents par défaut, ou `onComplete`).
//
// La caméra et le cadrage vivent désormais dans scan-stages.tsx : ils sont
// PARTAGÉS avec le parcours « Envoyer un document » du portail salarié
// (expense-scan-screen.tsx), qui y ajoute brouillon, gestion des pages et
// récapitulatif. Ici, le comportement est strictement celui d'avant.
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Trash, Plus, PaperPlaneRight as Send, CircleNotch as Loader2 } from '@phosphor-icons/react';
import { useToast } from '@/components/ui/toast-notifications';
import { useHistoryLayer } from './use-history-layer';
import { haptic } from '@/lib/portal-ux';
import type { PortalFetch } from './types';
import { CameraStage, CropStage, useCameraCapture, useScannerEngine, loadImage } from './scan-stages';
import { PageRetouche, REGLAGES_DEFAUT, type ReglagesPage } from './page-retouche';
import { diagnostiquerPage, type DiagnosticPage } from '@/lib/document-flatten';

// Une page conserve DEUX images : celle qu'on enverra, et la version non
// blanchie qui sert de matière première à la retouche (le blanchiment sature
// les niveaux et interdit toute mesure d'inclinaison).
interface PageScannee {
  finale: string;
  source: string;
  reglages: ReglagesPage;
  diag?: DiagnosticPage;
}

interface Props {
  // Portail salarié : envoi par défaut vers /hr/portal/documents (portalFetch requis).
  portalFetch?: PortalFetch;
  // Réutilisation (inbox back-office, rachats clients…) : si fourni, REMPLACE
  // l'envoi portail. Reçoit les pages (dataURI JPEG), renvoie { error } éventuel.
  onComplete?: (pages: string[]) => Promise<{ error?: string }>;
  // Titre du header (défaut « Scanner un frais »).
  title?: string;
  // Champs additionnels sur l'écran de revue (société, commentaire…) + blocage
  // d'envoi tant que requis non remplis + libellé du bouton.
  reviewExtras?: React.ReactNode;
  canSend?: boolean;
  sendLabel?: string;
  // Message affiché sous le bouton quand canSend est faux (ce qui manque).
  sendBlockedHint?: string;
  onBack: () => void;
  onSuccess: () => void;
}

type Stage = 'camera' | 'crop' | 'pages';

export function ScanFeeScreen({ portalFetch, onComplete, title, reviewExtras, canSend, sendLabel, sendBlockedHint, onBack, onSuccess }: Props) {
  const toast = useToast();
  const [stage, setStage] = useState<Stage>('camera');
  const [pages, setPages] = useState<PageScannee[]>([]);
  const [retouche, setRetouche] = useState<number | null>(null); // index de la page ouverte
  const [sending, setSending] = useState(false);

  // Geste retour système : recadrage -> caméra, pages -> caméra, caméra -> sortie.
  useHistoryLayer(stage === 'crop', () => setStage('camera'));
  useHistoryLayer(stage === 'pages', () => setStage('camera'));

  const { mlSession, mlLoading, cvOk } = useScannerEngine();
  const {
    videoRef, camError, capturing, setCapturing, startCamera, stopCamera, grabFrame,
  } = useCameraCapture(stage === 'camera');
  const [capturedImage, setCapturedImage] = useState<HTMLImageElement | null>(null);

  const goToCrop = useCallback((img: HTMLImageElement) => {
    stopCamera();
    setCapturedImage(img);
    setStage('crop');
    haptic('tap');
  }, [stopCamera]);

  const capture = useCallback(async () => {
    if (capturing) return;
    setCapturing(true);
    const img = await grabFrame();
    setCapturing(false);
    if (img) goToCrop(img);
  }, [grabFrame, goToCrop, capturing, setCapturing]);

  // Import galerie (photo déjà prise / PDF reçu par mail -> image).
  const onGalleryFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.push({ kind: 'warning', title: 'Format non géré', description: 'Choisis une photo (JPEG, PNG, HEIC).' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      void loadImage(reader.result as string).then((img) => { if (img) goToCrop(img); });
    };
    reader.readAsDataURL(file);
  }, [goToCrop, toast]);

  // Caméra : PLEIN ÉCRAN (sans header partagé), contrôles flottants.
  if (stage === 'camera') {
    return (
      <CameraStage
        videoRef={videoRef}
        camError={camError}
        capturing={capturing}
        onShutter={capture}
        onGalleryFile={onGalleryFile}
        onRetryCamera={startCamera}
        onBack={onBack}
        pagesCount={pages.length}
        onSeePages={() => { if (pages.length > 0) setStage('pages'); }}
      />
    );
  }

  // Recadrage / pages : header classique.
  //
  // HAUTEUR FIXE (h-dvh), pas min-h-dvh : ce conteneur est posé en surcouche
  // fixe par ses consommateurs (inbox, caisse). Avec min-h-dvh il grandissait
  // avec son contenu et tout ce qui dépassait l'écran (commentaire, bouton
  // Envoyer) était coupé, sans aucun moyen de défiler dans la PWA Inbox
  // (constaté le 20/09/2026). Désormais : la liste des pages défile à
  // l'intérieur, le bouton Envoyer reste épinglé en bas, toujours atteignable.
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-[#0B0C0E]">
      {/* Header */}
      <div
        className="relative z-[5] flex flex-shrink-0 items-center gap-2.5 border-b border-[#1F2127] bg-[#0B0C0E] px-5 pb-3.5"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 18px)' }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Retour"
          className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] border border-[#1F2127] bg-[#15171A] text-[#E8E9EB] transition-all duration-150 hover:bg-[#1A1D22] active:scale-95"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="text-[17px] font-semibold text-[#E8E9EB]" style={{ letterSpacing: '-0.01em' }}>
          {title ?? 'Scanner un frais'}
        </div>
        {pages.length > 0 && (
          <span className="ml-auto inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#8B5CF6]/20 px-2 text-[11px] font-semibold text-[#A78BFA]">
            {pages.length} page{pages.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {stage === 'crop' && capturedImage && (
        <CropStage
          image={capturedImage}
          mlSession={mlSession}
          mlLoading={mlLoading}
          cvOk={cvOk}
          onCancel={() => setStage(pages.length > 0 ? 'pages' : 'camera')}
          onValidated={(pageDataUri, _quad, _src, avantBlanchiment) => {
            const page: PageScannee = {
              finale: pageDataUri,
              source: avantBlanchiment ?? pageDataUri,
              reglages: { ...REGLAGES_DEFAUT },
            };
            setPages((p) => [...p, page]);
            haptic('success');
            setStage('pages');
          }}
        />
      )}

      {retouche !== null && pages[retouche] && (
        <PageRetouche
          numero={retouche + 1}
          source={pages[retouche].source}
          reglagesInitiaux={pages[retouche].reglages}
          onAnnuler={() => setRetouche(null)}
          onRefaire={() => { setPages((p) => p.filter((_, idx) => idx !== retouche)); setRetouche(null); setStage('camera'); }}
          onAppliquer={(finale, reglages) => {
            setPages((p) => p.map((page, idx) => (idx === retouche ? { ...page, finale, reglages, diag: undefined } : page)));
            setRetouche(null);
          }}
        />
      )}

      {stage === 'pages' && (
        <PagesStage
          pages={pages}
          sending={sending}
          reviewExtras={reviewExtras}
          canSend={canSend}
          sendLabel={sendLabel}
          sendBlockedHint={sendBlockedHint}
          onAddPage={() => setStage('camera')}
          onRemove={(i) => setPages((p) => p.filter((_, idx) => idx !== i))}
          onOuvrir={(i) => setRetouche(i)}
          onSend={async () => {
            if (pages.length === 0 || sending) return;
            setSending(true);
            // onComplete (réutilisation inbox/rachats) prioritaire ; sinon envoi
            // portail par défaut. Garde-fou si aucun mode n'est configuré.
            const images = pages.map((p) => p.finale);
            const { error } = onComplete
              ? await onComplete(images)
              : portalFetch
                ? await portalFetch('/hr/portal/documents', { method: 'POST', body: JSON.stringify({ pages: images }) })
                : { error: "Aucun mode d'envoi configuré" };
            setSending(false);
            if (error) {
              toast.push({ kind: 'warning', title: 'Envoi impossible', description: error });
              return;
            }
            haptic('success');
            onSuccess();
          }}
        />
      )}
    </div>
  );
}

// ── Étape pages (récap + envoi) ────────────────────────────────

function PagesStage({
  pages, sending, onAddPage, onRemove, onOuvrir, onSend, reviewExtras, canSend = true, sendLabel, sendBlockedHint,
}: {
  pages: PageScannee[];
  sending: boolean;
  onOuvrir: (i: number) => void;
  onAddPage: () => void;
  onRemove: (i: number) => void;
  onSend: () => void;
  // Champs additionnels (société, commentaire…) injectés par le consommateur
  // (inbox back-office). Rendus sous les pages, au-dessus du bouton Envoyer.
  reviewExtras?: React.ReactNode;
  // Bloque l'envoi tant que les champs requis ne sont pas remplis (ex société).
  canSend?: boolean;
  sendLabel?: string;
  // Pourquoi l'envoi est bloqué (ex « Choisis la société concernée »).
  sendBlockedHint?: string;
}) {
  // Diagnostic en tâche de fond : la grille s'affiche tout de suite, les
  // signalements arrivent ensuite. On ne demande à personne d'inspecter dix
  // tickets pour en trouver un de travers.
  const [diags, setDiags] = useState<Record<number, DiagnosticPage>>({});
  useEffect(() => {
    let annule = false;
    (async () => {
      for (let i = 0; i < pages.length; i++) {
        if (annule) return;
        if (diags[i]) continue;
        const im = await loadImage(pages[i].source);
        if (annule || !im) continue;
        const ech = Math.min(1, 900 / Math.max(im.naturalWidth, im.naturalHeight));
        const c = document.createElement('canvas');
        c.width = Math.max(20, Math.round(im.naturalWidth * ech));
        c.height = Math.max(20, Math.round(im.naturalHeight * ech));
        c.getContext('2d', { willReadFrequently: true })!.drawImage(im, 0, 0, c.width, c.height);
        const d = diagnostiquerPage(c);
        if (!annule) setDiags((prec) => ({ ...prec, [i]: d }));
      }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* min-h-0 obligatoire : sans lui un enfant flex refuse de rétrécir sous
          la taille de son contenu et la zone ne défile jamais. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5">
        <p className="mb-4 text-[12.5px] leading-relaxed text-[#8A8F98]">
          Vérifie tes pages avant l&apos;envoi. Appuie sur une page pour l&apos;ouvrir en grand : tu y trouveras les
          outils pour la redresser, la recadrer ou l&apos;éclaircir. Une page signalée en orange mérite un coup d&apos;oeil.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {pages.map((p, i) => (
            <div
              key={i}
              className={`relative overflow-hidden rounded-xl border bg-[#15171A] ${
                diags[i]?.soucis.length ? 'border-[#B45309]' : 'border-[#2A2D35]'
              }`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={p.finale}
                alt={`Page ${i + 1}`}
                onClick={() => onOuvrir(i)}
                className="h-44 w-full cursor-pointer object-cover"
              />
              <div className="absolute left-2 top-2 rounded-md bg-black/65 px-2 py-0.5 text-[10.5px] font-semibold text-white">
                Page {i + 1}
              </div>
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label={`Supprimer la page ${i + 1}`}
                className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-black/65 text-[#FCA5A5] active:scale-90"
              >
                <Trash size={15} />
              </button>
              {diags[i]?.soucis.length ? (
                <span className="absolute bottom-9 left-2 inline-flex items-center rounded-full bg-[#B45309]/95 px-2.5 py-0.5 text-[10.5px] font-bold text-white">
                  À vérifier
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => onOuvrir(i)}
                className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent py-2 text-[11.5px] font-semibold text-white"
              >
                Ouvrir et retoucher
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={onAddPage}
            className="flex h-44 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#2A2D35] text-[#8A8F98] active:bg-[#15171A]"
          >
            <Plus size={22} />
            <span className="text-[12px] font-semibold">Ajouter une page</span>
          </button>
        </div>

        {reviewExtras && <div className="mt-5 pb-2">{reviewExtras}</div>}
      </div>

      <div
        className="flex-shrink-0 border-t border-[#1F2127] bg-[#0B0C0E] px-5 py-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <button
          type="button"
          onClick={onSend}
          disabled={pages.length === 0 || sending || !canSend}
          className="inline-flex min-h-[50px] w-full items-center justify-center gap-2 rounded-xl px-4 text-[14.5px] font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-50"
          style={{ background: 'linear-gradient(180deg, #8B5CF6, #7C3AED)', boxShadow: '0 10px 24px -8px rgba(139,92,246,0.45)' }}
        >
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          {sending ? 'Envoi…' : (sendLabel ?? `Envoyer ${pages.length > 1 ? `les ${pages.length} pages` : 'le justificatif'}`)}
        </button>
        {/* Bouton grisé sans explication = utilisateur bloqué sans savoir pourquoi.
            On dit ce qui manque (le consommateur fournit le libellé). */}
        {!canSend && pages.length > 0 && sendBlockedHint && (
          <p className="mt-2 text-center text-[11.5px] text-[#FBBF24]">{sendBlockedHint}</p>
        )}
      </div>
    </div>
  );
}
